import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Plus, Trash2 } from 'lucide-react'
import { useId, useMemo, useState } from 'react'
import { Card, CardHeader } from '@/components/ui/Card'
import { Money } from '@/components/ui/Money'
import { PageHeader } from '@/components/ui/PageHeader'
import { SearchSelect } from '@/components/ui/SearchSelect'
import { toDecimal, toNumber } from '@/lib/decimal'
import { formatEuros } from '@/lib/format'
import { invoiceApi } from '@/lib/invoice-api'
import { useCreateCustom, useInvoiceClients, useInvoiceTemplates } from '@/lib/invoice-queries'
import { useBeneficiaries } from '@/lib/recurring-api'
import type { CustomInvoiceInput, DistributionMode, LineType } from '@/lib/invoice-types'
import { useDebouncedValue } from '@/lib/useDebouncedValue'
import { cn } from '@/lib/utils'

interface EditLine {
  key: string
  description: string
  quantity: string
  unitPrice: string
  amount: string
  lineType: LineType
}
interface EditGroup {
  key: string
  heading: string
  lines: EditLine[]
}
interface ManualRow {
  key: string
  beneficiary: string
  amount: string
}

let k = 0
function key(): string {
  k += 1
  return `k${k}`
}
const parse = (v: string) => toNumber(v.replace(',', '.'))

function newLine(): EditLine {
  return { key: key(), description: '', quantity: '', unitPrice: '', amount: '0', lineType: 'HOURS' }
}

export function InvoiceNewPage() {
  const clientLabelId = useId()
  const navigate = useNavigate()
  const clientsQuery = useInvoiceClients()
  const templatesQuery = useInvoiceTemplates()
  const createCustom = useCreateCustom()

  const [clientId, setClientId] = useState('')
  const [subject, setSubject] = useState('')
  const [groups, setGroups] = useState<EditGroup[]>([{ key: key(), heading: 'Werkzaamheden', lines: [newLine()] }])
  const [mode, setMode] = useState<DistributionMode>('CLIENT_SCHEME')
  const [manual, setManual] = useState<ManualRow[]>([{ key: key(), beneficiary: '', amount: '' }])

  const client = clientsQuery.data?.find((c) => c.id === clientId)
  const template = templatesQuery.data?.find((t) => t.code === client?.templateCode)

  const subtotal = groups.reduce((s, g) => s + g.lines.reduce((ls, l) => ls + parse(l.amount), 0), 0)
  const vatPct = client ? toNumber(client.vatPercentage) : 21
  const vat = client?.vatReversed ? 0 : (subtotal * vatPct) / 100
  const total = subtotal + vat
  const manualSum = manual.reduce((s, m) => s + parse(m.amount), 0)
  const { data: beneficiaries = [] } = useBeneficiaries()
  const beneficiaryOptions = useMemo(
    () => beneficiaries.map((b) => ({ value: b.id, label: b.label })),
    [beneficiaries],
  )

  const manualValid =
    mode !== 'MANUAL' ||
    (Math.abs(manualSum - subtotal) < 0.005 && manual.every((m) => m.beneficiary !== ''))

  function updateLine(gi: number, li: number, patch: Partial<EditLine>) {
    setGroups((prev) =>
      prev.map((g, i) =>
        i !== gi
          ? g
          : {
              ...g,
              lines: g.lines.map((l, j) => {
                if (j !== li) return l
                const merged = { ...l, ...patch }
                if ((patch.quantity !== undefined || patch.unitPrice !== undefined) && merged.quantity !== '' && merged.unitPrice !== '') {
                  merged.amount = toDecimal(parse(merged.quantity) * parse(merged.unitPrice))
                }
                return merged
              }),
            },
      ),
    )
  }

  const previewInput: CustomInvoiceInput | null = useMemo(() => {
    if (!client) return null
    if (mode === 'MANUAL' && !manualValid) return null
    return {
      clientId: client.id,
      subject: subject.trim() || 'Concept',
      groups: groups.map((g) => ({
        heading: g.heading,
        lines: g.lines.map((l) => ({
          description: l.description,
          quantity: l.quantity === '' ? null : toDecimal(parse(l.quantity)),
          unitPrice: l.unitPrice === '' ? null : toDecimal(parse(l.unitPrice)),
          amount: toDecimal(parse(l.amount)),
          lineType: l.lineType,
        })),
      })),
      distributionMode: mode,
      manualDistribution: mode === 'MANUAL' ? manual.map((m) => ({ beneficiary: m.beneficiary, amount: toDecimal(parse(m.amount)) })) : undefined,
    }
  }, [client, groups, subject, mode, manual, manualValid])

  const debouncedInput = useDebouncedValue(previewInput, 600)
  const previewQuery = useQuery({
    queryKey: ['invoices', 'custom-preview', debouncedInput],
    queryFn: () => invoiceApi.customPreviewHtml(debouncedInput as CustomInvoiceInput),
    enabled: debouncedInput !== null,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: false,
  })

  function save() {
    if (!client || !manualValid) return
    createCustom.mutate(
      {
        clientId: client.id,
        subject: subject.trim() || 'Custom factuur',
        groups: groups.map((g) => ({
          heading: g.heading,
          lines: g.lines.map((l) => ({
            description: l.description,
            quantity: l.quantity === '' ? null : toDecimal(parse(l.quantity)),
            unitPrice: l.unitPrice === '' ? null : toDecimal(parse(l.unitPrice)),
            amount: toDecimal(parse(l.amount)),
            lineType: l.lineType,
          })),
        })),
        distributionMode: mode,
        manualDistribution: mode === 'MANUAL' ? manual.map((m) => ({ beneficiary: m.beneficiary, amount: toDecimal(parse(m.amount)) })) : undefined,
      },
      { onSuccess: (inv) => navigate({ to: '/facturen/$id', params: { id: inv.id } }) },
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/facturen" className="mb-3 inline-flex items-center gap-1.5 text-sm text-ink-faint hover:text-ink">
          <ArrowLeft className="h-4 w-4" />
          Facturen
        </Link>
        <PageHeader title="Custom factuur bouwen" description="Formulier links, live preview rechts. De klant bepaalt huisstijl, adres en BTW-behandeling." />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)]">
        <div className="space-y-6">
          <Card>
            <CardHeader title="Klant en betreft" />
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="block">
                <span id={clientLabelId} className="mb-1.5 block text-sm font-medium text-ink-soft">Klant</span>
                <SearchSelect
                  value={clientId}
                  onValueChange={setClientId}
                  options={clientsQuery.data?.map((c) => ({ value: c.id, label: c.name, lastActivityAt: c.lastActivityAt })) ?? []}
                  placeholder={clientsQuery.isLoading ? 'Laden...' : 'Kies een klant…'}
                  searchPlaceholder="Zoek klant…"
                  emptyLabel="Geen klant gevonden."
                  disabled={clientsQuery.isLoading}
                  ariaLabelledby={clientLabelId}
                  className="w-full"
                />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink-soft">Betreft</span>
                <input value={subject} onChange={(e) => setSubject(e.target.value)} className="field" placeholder="Omschrijving op de factuur" />
              </label>
            </div>
            {client ? (
              <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-hairline bg-fill-1 px-3 py-2 text-sm text-ink-soft">
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: client.templateColor }} />
                  {template?.displayName ?? client.templateCode}
                </span>
                <span className="text-ink-faint">·</span>
                <span>{client.vatReversed ? 'BTW verlegd' : `BTW ${toNumber(client.vatPercentage)}%`}</span>
              </div>
            ) : null}
          </Card>

          <Card>
            <CardHeader
              title="Regels"
              action={
                <button type="button" className="btn-ghost h-8 px-2 text-sm" onClick={() => setGroups((p) => [...p, { key: key(), heading: 'Nieuwe groep', lines: [newLine()] }])}>
                  <Plus className="h-4 w-4" />
                  Groep
                </button>
              }
            />
            <div className="mt-4 space-y-4">
              {groups.map((group, gi) => (
                <div key={group.key} className="overflow-hidden rounded-lg border border-hairline">
                  <div className="flex items-center gap-2 border-b border-hairline bg-fill-1 px-3 py-2">
                    <input
                      value={group.heading}
                      onChange={(e) => setGroups((p) => p.map((g, i) => (i === gi ? { ...g, heading: e.target.value } : g)))}
                      className="flex-1 bg-transparent text-sm font-semibold text-ink outline-hidden"
                    />
                    <button type="button" className="btn-ghost h-7 w-7 p-0 text-ink-faint" onClick={() => setGroups((p) => p.filter((_, i) => i !== gi))} aria-label="Groep verwijderen">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="divide-y divide-hairline">
                    {group.lines.map((line, li) => (
                      <div key={line.key} className="grid grid-cols-[repeat(3,minmax(0,1fr))_auto] items-center gap-2 px-3 py-2 sm:grid-cols-[1fr_3.5rem_4.5rem_5rem_auto]">
                        <input value={line.description} onChange={(e) => updateLine(gi, li, { description: e.target.value })} placeholder="Omschrijving" className="field col-span-3 col-start-1 row-start-1 h-8 py-0 text-sm sm:col-span-1 sm:col-start-1 sm:row-start-1" />
                        <input value={line.quantity} onChange={(e) => updateLine(gi, li, { quantity: e.target.value })} inputMode="decimal" placeholder="aantal" className="field num col-start-1 row-start-2 h-8 py-0 text-right text-sm sm:col-start-2 sm:row-start-1" />
                        <input value={line.unitPrice} onChange={(e) => updateLine(gi, li, { unitPrice: e.target.value })} inputMode="decimal" placeholder="tarief" className="field num col-start-2 row-start-2 h-8 py-0 text-right text-sm sm:col-start-3 sm:row-start-1" />
                        <input value={line.amount} onChange={(e) => updateLine(gi, li, { amount: e.target.value })} inputMode="decimal" className="field num col-start-3 row-start-2 h-8 py-0 text-right text-sm sm:col-start-4 sm:row-start-1" />
                        <button type="button" className="btn-ghost col-start-4 row-start-1 h-7 w-7 p-0 text-ink-faint sm:col-start-5 sm:row-start-1" onClick={() => setGroups((p) => p.map((g, i) => (i === gi ? { ...g, lines: g.lines.filter((_, j) => j !== li) } : g)))} aria-label="Regel verwijderen">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button type="button" onClick={() => setGroups((p) => p.map((g, i) => (i === gi ? { ...g, lines: [...g.lines, newLine()] } : g)))} className="flex w-full items-center gap-1.5 px-3 py-2 text-sm font-medium text-accent-700 hover:bg-fill-1">
                    <Plus className="h-3.5 w-3.5" />
                    Regel
                  </button>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-ink-faint">Tip: negatieve bedragen voor korting, 0-regels voor inbegrepen posten.</p>
          </Card>

          <Card>
            <CardHeader title="Verdeling" />
            <div className="mt-4 flex flex-wrap gap-2">
              {(
                [
                  { value: 'CLIENT_SCHEME', label: 'Volgens klant-sleutel' },
                  { value: 'MANUAL', label: 'Handmatig' },
                  { value: 'NONE', label: 'Geen' },
                ] as const
              ).map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setMode(option.value)}
                  className={cn('btn-secondary', mode === option.value && 'border-accent-500/50 bg-accent-600/5 text-accent-700')}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {mode === 'MANUAL' ? (
              <div className="mt-4 space-y-2">
                {manual.map((row, i) => (
                  <div key={row.key} className="flex items-center gap-2">
                    <SearchSelect
                      value={row.beneficiary}
                      onValueChange={(v) => setManual((p) => p.map((m, j) => (j === i ? { ...m, beneficiary: v } : m)))}
                      options={beneficiaryOptions}
                      placeholder="Begunstigde"
                      className="flex-1"
                    />
                    <input value={row.amount} onChange={(e) => setManual((p) => p.map((m, j) => (j === i ? { ...m, amount: e.target.value } : m)))} inputMode="decimal" placeholder="bedrag" className="field num w-28 text-right" />
                    <button type="button" className="btn-ghost h-8 w-8 p-0 text-ink-faint" onClick={() => setManual((p) => p.filter((_, j) => j !== i))} aria-label="Verwijderen">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                <div className="flex items-center justify-between">
                  <button type="button" className="btn-ghost text-sm" onClick={() => setManual((p) => [...p, { key: key(), beneficiary: '', amount: '' }])}>
                    <Plus className="h-4 w-4" />
                    Begunstigde
                  </button>
                  <span className={cn('num text-sm', manualValid ? 'text-ink-faint' : 'text-status-overdue')}>
                    {formatEuros(manualSum)} / {formatEuros(subtotal)}
                  </span>
                </div>
                {!manualValid ? <p className="text-xs text-status-overdue">De som van de verdeling moet gelijk zijn aan het subtotaal.</p> : null}
              </div>
            ) : null}
          </Card>
        </div>

        <div className="lg:sticky lg:top-20 lg:self-start">
          <Card inset className="overflow-hidden">
            <div className="flex items-center justify-between border-b border-hairline bg-fill-1 px-4 py-2.5 text-xs font-medium text-ink-faint">
              <span>Preview</span>
              <Money amount={subtotal} secondaryAmount={total} className="text-ink-soft" />
            </div>
            <div className="relative aspect-[1/1.414] bg-white">
              {previewQuery.data ? (
                <iframe title="Preview" srcDoc={previewQuery.data} className="h-full w-full border-0" sandbox="" />
              ) : previewQuery.isError ? (
                <div className="flex h-full items-center justify-center px-8 text-center text-sm text-ink-faint">
                  De preview kon niet gerenderd worden. Controleer de regels en probeer opnieuw.
                </div>
              ) : previewQuery.isLoading && debouncedInput !== null ? (
                <div className="flex h-full items-center justify-center px-8 text-center text-sm text-ink-faint">Preview laden...</div>
              ) : (
                <div className="flex h-full items-center justify-center px-8 text-center text-sm text-ink-faint">
                  Kies een klant om de preview te tonen.
                </div>
              )}
              {previewQuery.isFetching && previewQuery.data ? (
                <div className="absolute right-3 top-3 h-2 w-2 animate-pulse rounded-full bg-accent-600" />
              ) : null}
            </div>
          </Card>
        </div>
      </div>

      <div className="sticky bottom-0 -mx-4 flex items-center justify-end gap-2 border-t border-hairline bg-canvas/85 px-4 py-3 backdrop-blur-xl lg:-mx-8 lg:px-8">
        <Link to="/facturen" className="btn-ghost">
          Annuleren
        </Link>
        <button type="button" className="btn-primary" onClick={save} disabled={!client || !manualValid || createCustom.isPending}>
          {createCustom.isPending ? 'Opslaan…' : 'Opslaan als concept'}
        </button>
      </div>
    </div>
  )
}
